import { defineStore } from 'pinia';
import { computed, watch } from 'vue';

import { useActiveIdentity } from '../auth0/composables/useActiveIdentity';
import { useAuthSessionStore } from '../auth0/store/auth-session.store';
import { onStaleDataRefetchKey, refetchKeys } from '../websocket';
import type {
  AccessDenialReason,
  AccessRequirement,
  AccessRequirementKey,
  AccessState,
} from './access-control.types';
import {
  getAccessRequirement,
  isResolvedSourceStatus,
  resolveAccessRequirements,
  resolveAccessState,
} from './access-control.utils';
import { useIfSourceStore } from './if-source.store';
import { useKybSourceStore } from './kyb-source.store';
import { useKycSourceStore } from './kyc-source.store';
import { usePhoneSourceStore } from './phone-source.store';
import { useUserContextStore } from './user-context.store';

const ORGANIZATION_SCOPE_RETRY_DELAY_MS = 1_000;
const MAX_ORGANIZATION_SCOPE_RETRY_ATTEMPTS = 3;
const ACCESS_DEBUG_STORAGE_KEY = 'peanuds:debug-access';
let hasRegisteredAccessRefreshListener = false;

type AccessDebugWindow = Window & {
  peanudsDebugAccess?: () => void;
};

const isAccessDebugEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const searchParams = new URLSearchParams(window.location.search);

  if (
    searchParams.get(ACCESS_DEBUG_STORAGE_KEY) === '1' ||
    searchParams.get('debugAccess') === '1'
  ) {
    return true;
  }

  try {
    return window.localStorage.getItem(ACCESS_DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const hasValue = (value: string | null | undefined) => {
  return typeof value === 'string' && value.length > 0;
};

export type {
  AccessDenialReason,
  AccessRequirement,
  AccessRequirementCtaKind,
  AccessRequirementKey,
  AccessRequirementState,
  AccessRequirementStatus,
  AccessState,
  IfState,
  IfStatusType,
  KybLoadStatus,
  KybState,
  KybStatusType,
  KycLoadStatus,
  KycState,
  KycStatusType,
  PhoneLoadStatus,
  PhoneState,
  PhoneStatusType,
} from './access-control.types';

export const useAccessControlStore = defineStore('access-control', () => {
  const authSessionStore = useAuthSessionStore();
  const kycSourceStore = useKycSourceStore();
  const kybSourceStore = useKybSourceStore();
  const phoneSourceStore = usePhoneSourceStore();
  const ifSourceStore = useIfSourceStore();
  const userContextStore = useUserContextStore();
  const activeIdentity = useActiveIdentity();
  const {
    resetKycState,
    fetchKycStatus: fetchKycStatusForUser,
    ensureKycResolved,
    generateKycLink,
  } = kycSourceStore;
  const { resetPhoneState, ensurePhoneResolved } = phoneSourceStore;
  const {
    resetKybState,
    fetchKybStatus: fetchKybStatusForUser,
    ensureKybResolved,
    generateKybLink,
  } = kybSourceStore;
  let organizationScopeRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let organizationScopeRetryAttempts = 0;
  let accessResolutionPromise: Promise<void> | null = null;

  type AccessResolutionIdentity = [
    hasSession: boolean,
    userId: string | null | undefined,
    hasEmailConfirmationClaim: boolean,
    isEmailConfirmed: boolean,
    isPhoneVerified: boolean,
    resolvedOrganizationId: string | null,
    requiresOrganizationSelection: boolean,
    isUserContextLoaded: boolean,
    sessionOrganizationId: string | null,
    isOrganizationScoped: boolean,
  ];

  const requiresExplicitOrganizationSelection = () => {
    return (
      userContextStore.requiresOrganizationSelection &&
      !(userContextStore.isLoaded && userContextStore.organizations.length === 1)
    );
  };

  const accessResolutionIdentity = (): AccessResolutionIdentity => [
    activeIdentity.hasSession.value,
    activeIdentity.userId.value,
    activeIdentity.hasEmailConfirmationClaim.value,
    activeIdentity.isEmailConfirmed.value,
    activeIdentity.isPhoneVerified.value,
    userContextStore.resolvedOrganizationId,
    requiresExplicitOrganizationSelection(),
    userContextStore.isLoaded,
    authSessionStore.organizationId,
    authSessionStore.isOrganizationScoped,
  ];

  const accessResolutionTrigger = () =>
    [
      ...accessResolutionIdentity(),
      phoneSourceStore.isPhoneVerified,
      kycSourceStore.kyc.status,
      kybSourceStore.kyb.status,
    ] as const;

  const hasAccessResolutionIdentityChanged = (
    nextIdentity: AccessResolutionIdentity,
    previousIdentity?: AccessResolutionIdentity,
  ) => {
    if (!previousIdentity) {
      return true;
    }

    return nextIdentity.some((value, index) => value !== previousIdentity[index]);
  };

  const clearOrganizationScopeRetry = () => {
    if (!organizationScopeRetryTimer) {
      return;
    }

    clearTimeout(organizationScopeRetryTimer);
    organizationScopeRetryTimer = null;
  };

  const resetOrganizationScopeRetry = () => {
    clearOrganizationScopeRetry();
    organizationScopeRetryAttempts = 0;
  };

  watch(
    accessResolutionIdentity,
    (nextIdentity, previousIdentity) => {
      if (!hasAccessResolutionIdentityChanged(nextIdentity, previousIdentity)) {
        return;
      }

      resetOrganizationScopeRetry();
      resetPhoneState();
      resetKycState();
      resetKybState();
    },
    { immediate: true },
  );

  const isAccessResolved = computed(() => {
    if (!authSessionStore.jwtToken) {
      return true;
    }

    if (!activeIdentity.hasEmailConfirmationClaim.value) {
      return false;
    }

    if (!activeIdentity.isEmailConfirmed.value) {
      return true;
    }

    if (!isResolvedSourceStatus(phoneSourceStore.phone.status)) {
      return false;
    }

    if (!phoneSourceStore.isPhoneVerified) {
      return true;
    }

    const hasNoOrganizationToResolve =
      userContextStore.isLoaded &&
      !userContextStore.requiresOrganizationSelection &&
      userContextStore.organizations.length === 0 &&
      userContextStore.resolvedOrganizationId === null;

    if (hasNoOrganizationToResolve) {
      return true;
    }

    return (
      isResolvedSourceStatus(kycSourceStore.kyc.status) &&
      isResolvedSourceStatus(kybSourceStore.kyb.status)
    );
  });

  const requirements = computed<AccessRequirement[]>(() => {
    if (!authSessionStore.jwtToken) {
      return [];
    }

    return resolveAccessRequirements({
      isEmailConfirmed: activeIdentity.isEmailConfirmed.value,
      isPhoneVerified: phoneSourceStore.isPhoneVerified,
      phoneSourceStatus: phoneSourceStore.phone.status,
      kycStatus: kycSourceStore.kyc.data?.kycStatus ?? null,
      kycSourceStatus: kycSourceStore.kyc.status,
      kybStatus: kybSourceStore.kyb.data?.kybStatus ?? null,
      kybSourceStatus: kybSourceStore.kyb.status,
      isIfSatisfied: ifSourceStore.isIfSatisfied,
    });
  });

  const blockingRequirements = computed(() => {
    return requirements.value.filter((requirement) => requirement.isBlocking);
  });

  const pendingRequirements = computed(() => {
    return requirements.value.filter((requirement) => requirement.state === 'pending');
  });

  const getRequirement = (key: AccessRequirementKey) => {
    return getAccessRequirement(requirements.value, key);
  };

  const accessState = computed<AccessState>(() =>
    resolveAccessState({
      hasJwtToken: !!authSessionStore.jwtToken,
      isAccessResolved: isAccessResolved.value,
      requirements: requirements.value,
    }),
  );

  const isOnboarded = computed(() => accessState.value === 'onboarded');

  const writeAccessDebugSnapshot = (trigger: string) => {
    console.warn('[access-control]', {
      trigger,
      accessState: accessState.value,
      isAccessResolved: isAccessResolved.value,
      isOnboarded: isOnboarded.value,
      denial:
        accessState.value === 'unauthenticated' || accessState.value === 'onboarded'
          ? null
          : {
              accessState: accessState.value,
              blockingRequirementKeys: blockingRequirements.value.map(
                (requirement) => requirement.key,
              ),
              pendingRequirementKeys: pendingRequirements.value.map(
                (requirement) => requirement.key,
              ),
            },
      requirements: requirements.value.map((requirement) => ({
        key: requirement.key,
        state: requirement.state,
        status: requirement.status,
        sourceStatus: requirement.sourceStatus,
        isBlocking: requirement.isBlocking,
      })),
      session: {
        hasJwtToken: hasValue(authSessionStore.jwtToken),
        hasIdToken: hasValue(authSessionStore.idToken),
        isTokenExpired: authSessionStore.isTokenExpired,
        isOrganizationScoped: authSessionStore.isOrganizationScoped,
        organizationIdPresent: hasValue(authSessionStore.organizationId),
        requiredOrganizationIdPresent: hasValue(authSessionStore.requiredOrganizationId),
        organizationMatchesRequired:
          !authSessionStore.requiredOrganizationId ||
          authSessionStore.organizationId === authSessionStore.requiredOrganizationId,
        ifClientIdPresent: hasValue(authSessionStore.ifClientId),
        ifExternalUserIdPresent: hasValue(authSessionStore.ifExternalUserId),
        isAuthenticatedInIf: authSessionStore.isAuthenticatedInIf,
      },
      userContext: {
        isLoaded: userContextStore.isLoaded,
        isLoading: userContextStore.isLoading,
        hasLoadError: userContextStore.loadError !== null,
        organizationCount: userContextStore.organizations.length,
        selectedOrganizationPresent: hasValue(userContextStore.selectedOrganizationId),
        resolvedOrganizationPresent: hasValue(userContextStore.resolvedOrganizationId),
        requiresOrganizationSelection: userContextStore.requiresOrganizationSelection,
        requiresOrganizationCreation: userContextStore.requiresOrganizationCreation,
      },
      sources: {
        phone: {
          sourceStatus: phoneSourceStore.phone.status,
          isVerified: phoneSourceStore.isPhoneVerified,
          hasPhoneNumber: hasValue(phoneSourceStore.phone.data?.phoneNumber),
        },
        kyc: {
          sourceStatus: kycSourceStore.kyc.status,
          status: kycSourceStore.kyc.data?.kycStatus ?? null,
          hasData: kycSourceStore.kyc.data !== null,
        },
        kyb: {
          sourceStatus: kybSourceStore.kyb.status,
          status: kybSourceStore.kyb.data?.kybStatus ?? null,
          hasData: kybSourceStore.kyb.data !== null,
        },
        if: {
          isSatisfied: ifSourceStore.isIfSatisfied,
          sourceStatus: ifSourceStore.ifSource.status,
        },
      },
    });
  };

  const logAccessDebugSnapshot = (trigger: string) => {
    if (!isAccessDebugEnabled()) {
      return;
    }

    writeAccessDebugSnapshot(trigger);
  };

  const canAccess = () => isOnboarded.value;

  const hasRequirementSatisfied = (key: AccessRequirementKey) => {
    return getRequirement(key)?.state === 'satisfied';
  };

  const getAccessDenialReason = (): AccessDenialReason | null => {
    if (accessState.value === 'unauthenticated' || accessState.value === 'onboarded') {
      return null;
    }

    return {
      accessState: accessState.value,
      blockingRequirementKeys: blockingRequirements.value.map((requirement) => requirement.key),
      pendingRequirementKeys: pendingRequirements.value.map((requirement) => requirement.key),
    };
  };

  const fetchKycStatus = async () => {
    const userId = activeIdentity.userId.value;
    if (!userId) return;

    await fetchKycStatusForUser(userId);
  };

  const fetchKybStatus = async () => {
    const userId = activeIdentity.userId.value;
    if (!userId) return;

    await fetchKybStatusForUser(userId);
  };

  const ensurePhoneStatus = async () => {
    await ensurePhoneResolved();
  };

  const openKycVerificationLink = async () => {
    const userId = activeIdentity.userId.value;

    if (!userId) {
      return;
    }

    const url = await generateKycLink(userId);

    if (!url) {
      return;
    }

    window.open(url, '_blank');
  };

  const openKybVerificationLink = async () => {
    const url = await generateKybLink();

    if (!url) {
      return;
    }

    window.open(url, '_blank');
  };

  const ensureAccessResolved = async () => {
    if (accessResolutionPromise) {
      return accessResolutionPromise;
    }

    accessResolutionPromise = (async () => {
      let userId = activeIdentity.userId.value;
      let targetOrganizationId = userContextStore.resolvedOrganizationId;
      let sessionOrganizationId = authSessionStore.organizationId;

      if (!activeIdentity.hasSession.value || !userId) {
        logAccessDebugSnapshot('ensureAccessResolved:missing-session-or-user');
        return;
      }

      if (
        !activeIdentity.hasEmailConfirmationClaim.value ||
        !activeIdentity.isEmailConfirmed.value
      ) {
        resetOrganizationScopeRetry();
        logAccessDebugSnapshot('ensureAccessResolved:email-not-confirmed');
        return;
      }

      await ensurePhoneResolved();

      if (!phoneSourceStore.isPhoneVerified) {
        resetOrganizationScopeRetry();
        logAccessDebugSnapshot('ensureAccessResolved:phone-not-verified');
        return;
      }

      if (!userContextStore.isLoaded) {
        logAccessDebugSnapshot('ensureAccessResolved:user-context-not-loaded');
        return;
      }

      if (requiresExplicitOrganizationSelection()) {
        resetOrganizationScopeRetry();
        logAccessDebugSnapshot('ensureAccessResolved:organization-selection-required');
        return;
      }

      if (!targetOrganizationId) {
        resetOrganizationScopeRetry();
        logAccessDebugSnapshot('ensureAccessResolved:missing-target-organization');
        return;
      }

      const scheduleOrganizationScopeRetry = () => {
        if (
          organizationScopeRetryTimer ||
          organizationScopeRetryAttempts >= MAX_ORGANIZATION_SCOPE_RETRY_ATTEMPTS ||
          !activeIdentity.hasSession.value ||
          !activeIdentity.userId.value ||
          requiresExplicitOrganizationSelection() ||
          !userContextStore.resolvedOrganizationId ||
          (authSessionStore.organizationId === userContextStore.resolvedOrganizationId &&
            authSessionStore.isOrganizationScoped)
        ) {
          return;
        }

        organizationScopeRetryTimer = setTimeout(() => {
          organizationScopeRetryTimer = null;
          organizationScopeRetryAttempts += 1;
          void ensureAccessResolved();
        }, ORGANIZATION_SCOPE_RETRY_DELAY_MS);
      };

      if (!authSessionStore.isOrganizationScoped || !sessionOrganizationId) {
        await authSessionStore.waitForAuthSync();

        userId = activeIdentity.userId.value;
        targetOrganizationId = userContextStore.resolvedOrganizationId;
        sessionOrganizationId = authSessionStore.organizationId;

        if (!activeIdentity.hasSession.value || !userId || !targetOrganizationId) {
          logAccessDebugSnapshot('ensureAccessResolved:session-lost-after-auth-sync');
          return;
        }
      }

      const isSessionReadyForOrganizationRequests =
        !!sessionOrganizationId &&
        sessionOrganizationId === targetOrganizationId &&
        (authSessionStore.isOrganizationScoped || userContextStore.organizations.length === 1);

      if (!isSessionReadyForOrganizationRequests) {
        scheduleOrganizationScopeRetry();
        logAccessDebugSnapshot('ensureAccessResolved:organization-session-not-ready');
        return;
      }

      resetOrganizationScopeRetry();

      await Promise.all([ensureKycResolved(userId), ensureKybResolved(userId)]);
      logAccessDebugSnapshot('ensureAccessResolved:verification-sources-resolved');
    })().finally(() => {
      accessResolutionPromise = null;
    });

    return accessResolutionPromise;
  };

  const refreshAccessResolution = async () => {
    resetOrganizationScopeRetry();
    resetPhoneState();
    resetKycState();
    resetKybState();

    await ensureAccessResolved();
  };

  const runKycCta = async () => {
    const requirement = getRequirement('kyc');

    if (!requirement) {
      return;
    }

    if (
      requirement.ctaKind !== 'start' &&
      requirement.ctaKind !== 'resume' &&
      requirement.ctaKind !== 'refresh'
    ) {
      return;
    }

    await openKycVerificationLink();
  };

  const runKybCta = async () => {
    const requirement = getRequirement('kyb');

    if (!requirement) {
      return;
    }

    if (
      requirement.ctaKind !== 'start' &&
      requirement.ctaKind !== 'resume' &&
      requirement.ctaKind !== 'refresh'
    ) {
      return;
    }

    await openKybVerificationLink();
  };

  watch(
    () => [
      accessState.value,
      isAccessResolved.value,
      isOnboarded.value,
      authSessionStore.isAuthenticatedInIf,
      authSessionStore.isOrganizationScoped,
      userContextStore.isLoaded,
      requiresExplicitOrganizationSelection(),
      phoneSourceStore.phone.status,
      phoneSourceStore.isPhoneVerified,
      kycSourceStore.kyc.status,
      kycSourceStore.kyc.data?.kycStatus ?? null,
      kybSourceStore.kyb.status,
      kybSourceStore.kyb.data?.kybStatus ?? null,
      ifSourceStore.isIfSatisfied,
    ],
    () => {
      logAccessDebugSnapshot('state-change');
    },
    { immediate: true },
  );

  watch(
    accessResolutionTrigger,
    ([
      hasSession,
      userId,
      hasEmailConfirmationClaim,
      isEmailConfirmed,
      _isPhoneVerified,
      organizationId,
      requiresOrganizationSelection,
      isUserContextLoaded,
      _sessionOrganizationId,
      _isOrganizationScoped,
    ]) => {
      if (!hasSession || !userId) {
        return;
      }

      if (!hasEmailConfirmationClaim || !isEmailConfirmed) {
        return;
      }

      void ensurePhoneStatus();

      if (!phoneSourceStore.isPhoneVerified) {
        return;
      }

      if (!isUserContextLoaded || requiresOrganizationSelection) {
        return;
      }

      if (!organizationId) {
        return;
      }

      const shouldResolveKyc = kycSourceStore.kyc.status === 'idle';
      const shouldResolveKyb = kybSourceStore.kyb.status === 'idle';

      if (!shouldResolveKyc && !shouldResolveKyb) {
        return;
      }

      void ensureAccessResolved();
    },
    { immediate: true },
  );

  if (typeof window !== 'undefined') {
    (window as AccessDebugWindow).peanudsDebugAccess = () => {
      writeAccessDebugSnapshot('manual');
    };

    if (isAccessDebugEnabled()) {
      console.warn(
        '[access-control] debug enabled. Run window.peanudsDebugAccess?.() for a snapshot.',
      );
    }
  }

  if (!hasRegisteredAccessRefreshListener) {
    hasRegisteredAccessRefreshListener = true;

    const refreshAccessResolution = () => {
      void useAccessControlStore().refreshAccessResolution();
    };

    const refreshAuthClaimsAndAccessResolution = async () => {
      await useAuthSessionStore().refreshAuthClaims();
      await useAccessControlStore().refreshAccessResolution();
    };

    onStaleDataRefetchKey(refetchKeys.KYC_STATUS, refreshAccessResolution);
    onStaleDataRefetchKey(refetchKeys.KYB_STATUS, refreshAccessResolution);
    onStaleDataRefetchKey(refetchKeys.AUTH_JWT, refreshAuthClaimsAndAccessResolution);
  }

  return {
    accessState,
    requirements,
    blockingRequirements,
    pendingRequirements,
    phone: phoneSourceStore.phone,
    ifSource: ifSourceStore.ifSource,
    isAccessResolved,
    isOnboarded,
    canAccess,
    getRequirement,
    hasRequirementSatisfied,
    getAccessDenialReason,
    ensurePhoneStatus,
    fetchKycStatus,
    fetchKybStatus,
    openKycVerificationLink,
    openKybVerificationLink,
    ensureAccessResolved,
    refreshAccessResolution,
    runKycCta,
    runKybCta,
  };
});
